/**
 * One global price (priceUsd) shown in the buyer's local currency.
 * Razorpay always charges the INR equivalent (international cards pay in INR).
 */
import regionsJson from "../../../pricing/regions.json";

export type PriceRegionId = keyof typeof regionsJson.full_course.regions;

export type RegionalPrice = {
  regionId: PriceRegionId;
  label: string;
  currency: string;
  displayAmount: number;
  chargeAmountInr: number;
};

const FULL = regionsJson.full_course;

export const FULL_COURSE_PRICE_USD = FULL.priceUsd;
export const FULL_COURSE_CHARGE_INR = Math.round(FULL.priceUsd * FULL.usdToInr);

function regionEntry(id: PriceRegionId): RegionalPrice {
  const r = FULL.regions[id];
  return {
    regionId: id,
    label: r.label,
    currency: r.currency,
    displayAmount: Math.round(FULL.priceUsd * r.usdRate),
    chargeAmountInr: FULL_COURSE_CHARGE_INR,
  };
}

/** Detect buyer region from timezone + browser locale (no paid geo API). */
export function detectPriceRegion(): PriceRegionId {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    for (const [id, r] of Object.entries(FULL.regions)) {
      const tzList = (r as { timezones?: string[] }).timezones;
      if (tzList?.includes(tz)) return id as PriceRegionId;
    }
  } catch {
    /* ignore */
  }

  const locales = [
    navigator.language,
    ...(navigator.languages ?? []),
  ].filter(Boolean) as string[];

  for (const loc of locales) {
    const parts = loc.split("-");
    const country = parts.length >= 2 ? parts[parts.length - 1].toUpperCase() : "";
    if (!country) continue;
    for (const [id, r] of Object.entries(FULL.regions)) {
      if (id === "ROW") continue;
      if ((r as { countries: string[] }).countries.includes(country)) {
        return id as PriceRegionId;
      }
    }
  }

  return FULL.defaultRegion as PriceRegionId;
}

export function getFullCourseRegionalPrice(regionId?: PriceRegionId): RegionalPrice {
  const id = regionId ?? detectPriceRegion();
  return regionEntry(id);
}

export function formatRegionalPrice(price: RegionalPrice, locale?: string): string {
  const loc = locale || navigator.language || "en-US";
  try {
    return new Intl.NumberFormat(loc, {
      style: "currency",
      currency: price.currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price.displayAmount);
  } catch {
    return `${price.currency} ${price.displayAmount}`;
  }
}

export function formatBuyLabel(productId: ShopProductId, regionId?: PriceRegionId): string {
  if (productId === "full_course") {
    const p = getFullCourseRegionalPrice(regionId);
    return `Buy · ${formatRegionalPrice(p)}`;
  }
  return "Buy";
}

export function listFullCourseChargeAmountsInr(): number[] {
  return [FULL_COURSE_CHARGE_INR];
}

export type ShopProductId = "full_course" | "hd_sample_pack";

/** Human-readable price table for support / FAQ. */
export function fullCoursePriceTable(): Array<{
  region: string;
  currency: string;
  display: string;
  chargeInr: number;
}> {
  return (Object.keys(FULL.regions) as PriceRegionId[]).map((id) => {
    const p = regionEntry(id);
    return {
      region: p.label || id,
      currency: p.currency,
      display: formatRegionalPrice(p),
      chargeInr: p.chargeAmountInr,
    };
  });
}
